import type { AgentEditChromeRange } from '@atlaskit/editor-common/collab-agent-edit-chrome';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

const clampToDoc = (doc: PMNode, pos: number): number =>
	Math.min(Math.max(pos, 0), doc.content.size);

/**
 * Selects the full inline content of affected textblocks, including nested paragraphs
 * and task items. Whole-container inputs visit all their descendant textblocks.
 * The renderer continues to use its existing inline highlight and single trailing cursor.
 */
export const normalizeAgentEditChromeRanges = (
	doc: PMNode,
	ranges: AgentEditChromeRange[],
): AgentEditChromeRange[] => {
	type BlockRange = AgentEditChromeRange & { outerFrom: number; outerTo: number };
	const blocks = new Map<number, BlockRange>();
	for (const { from, to } of ranges) {
		if (!Number.isFinite(from) || !Number.isFinite(to)) {
			continue;
		}
		const start = clampToDoc(doc, Math.min(from, to));
		const end = clampToDoc(doc, Math.max(from, to));
		// A collapsed/deleted range has no surviving content to select.
		if (start === end) {
			continue;
		}
		// nodesBetween uses an exclusive end: a sibling starting at end is untouched.
		doc.nodesBetween(start, end, (node, pos) => {
			if (node.isTextblock) {
				if (node.content.size > 0) {
					blocks.set(pos, {
						// Skip the node's opening (+1) and closing (-1) boundaries.
						from: pos + 1,
						to: pos + node.nodeSize - 1,
						outerFrom: pos,
						outerTo: pos + node.nodeSize,
					});
				}
				return false;
			}
		});
	}

	const groups: BlockRange[] = [];
	for (const block of [...blocks.values()].sort((left, right) => left.from - right.from)) {
		const previous = groups[groups.length - 1];
		// Coalesce immediately adjacent blocks using their OUTER bounds. Content bounds
		// exclude node boundaries, so comparing those would miss adjacent paragraphs.
		// An untouched sibling, empty block or leaf node leaves a gap and is not bridged.
		if (previous && block.outerFrom === previous.outerTo) {
			previous.to = block.to;
			previous.outerTo = block.outerTo;
		} else {
			groups.push({ ...block });
		}
	}
	return groups.map(({ from, to }) => ({ from, to }));
};
